import React, { useCallback, useEffect, useId, useRef, useState } from "react";

// VOUCH MARK — outline hexagon with a V inside. A real member's credibility standing behind an
// invitee. Design is LOCKED: never filled, no background, no container beyond the hexagon itself.
//
// Stroke weight SCALES INVERSELY with size — a thin line at 12px fills the hexagon into mush, so
// the stroke is DERIVED from `size` and can never be passed in. Anchor points (px → stroke):
//   24 → 1.6 · 18 → 1.8 · 16 → 1.9 · 14 → 2.1 · 13 → 2.2 · 12 → 2.4
const STROKE_POINTS = [[12, 2.4], [13, 2.2], [14, 2.1], [16, 1.9], [18, 1.8], [24, 1.6]];
function strokeForSize(size) {
  if (size <= 12) return 2.4;
  if (size >= 24) return 1.6;
  for (let i = 0; i < STROKE_POINTS.length - 1; i++) {
    const [s0, w0] = STROKE_POINTS[i];
    const [s1, w1] = STROKE_POINTS[i + 1];
    if (size >= s0 && size <= s1) {
      const t = (size - s0) / (s1 - s0);
      return Math.round((w0 + (w1 - w0) * t) * 100) / 100;
    }
  }
  return 1.9;
}

const VOUCH_BLUE = "#1F6BA8";

// Renders ONLY when the invitee's vouch is live (gating is upstream — this component just renders
// what it is handed). Travels WITH the handle; always last in the row (after the verification chip
// and the founding plate). It is not a chip and never counts against the one-filled-chip rule.
// `spacing` is the fixed gap (px) between the preceding element and the mark — LOCKED at 14 globally,
// baked into the component so it never depends on a host row's flex gap. Pass 0 only for isolated demos.
export function VouchMark({ inviter, size = 16, testid = "vouch-mark", className = "", spacing = 14 }) {
  const handle = (inviter || "").replace(/^@/, "");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const tipId = useId();

  const close = useCallback(() => setOpen(false), []);

  // Dismiss on tap/click outside (touch: tap-again-to-dismiss is handled by the toggle below).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    document.addEventListener("pointerdown", onDoc, true);
    return () => document.removeEventListener("pointerdown", onDoc, true);
  }, [open, close]);

  if (!handle) return null;

  const stroke = strokeForSize(size);
  const label = `Vouched for by @${handle}`;

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex items-center align-middle ${className}`}
      style={{ lineHeight: 0, marginLeft: spacing }}
    >
      <span
        role="img"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        tabIndex={0}
        data-testid={testid}
        data-vouch-inviter={handle}
        className="inline-flex items-center outline-none cursor-default focus-visible:ring-1 focus-visible:ring-[#1F6BA8]"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); }
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); }
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={VOUCH_BLUE}
          strokeWidth={stroke}
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ display: "block", overflow: "visible" }}
        >
          <path d="M12 2.2 20.5 7v10L12 21.8 3.5 17V7z" />
          <path d="M8.6 9.2 12 16.2l3.4-7" strokeLinecap="round" />
        </svg>
      </span>
      {open && (
        <span
          id={tipId}
          role="tooltip"
          data-testid={`${testid}-tooltip`}
          className="absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+6px)] z-50 whitespace-nowrap pointer-events-none
                     font-mono text-[11px] tracking-wide px-2.5 py-1.5 border shadow-lg"
          style={{ backgroundColor: "#111213", borderColor: "#2E2F31", color: "#DAD6CE" }}
        >
          Vouched for by <span style={{ color: "#79B8E4" }}>@{handle}</span>
        </span>
      )}
    </span>
  );
}

export default VouchMark;
